"use client"

import type React from "react"
import type { Column } from "@tanstack/react-table"
import { ChevronsUpDown, EyeOff, SortAsc, SortDesc } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../dropdown-menu"
import { Button } from "../button"
import { cn } from "../../../utils/utils"
import { Check } from "lucide-react"

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>
  title: string
  showSortingOptions?: boolean
  enumOptions?: { label: string; value: string }[]
  onEnumFilter?: (value: string) => void
  selectedEnumValue?: string // <-- Add this
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
  showSortingOptions = true,
  enumOptions,
  onEnumFilter,
  selectedEnumValue,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const hasMenu =
    (showSortingOptions && column.getCanSort()) ||
    (enumOptions && onEnumFilter);

  if (!hasMenu) {
    return <span className={cn("font-semibold", className)}>{title}</span>;
  }

  return (
    <div className={cn("flex items-center space-x-2", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "-ml-3 h-8 data-[state=open]:bg-accent",
              "cursor-pointer hover:bg-accent/40"
            )}
          >
            <span>{title}</span>
            {/* Always show a dropdown icon if menu is available */}
            <ChevronsUpDown className="ml-2 h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {showSortingOptions && column.getCanSort() && (
            <>
              <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
                <SortAsc className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                Asc
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
                <SortDesc className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                Desc
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
                <EyeOff className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                Hide
              </DropdownMenuItem>
            </>
          )}
          {enumOptions && onEnumFilter && (
            <>
              <div className="px-2 py-1 text-xs text-muted-foreground">Filter by:</div>
              {enumOptions.map(opt => (
                <DropdownMenuItem key={opt.value} onClick={() => onEnumFilter(opt.value)}>
                  {opt.label}
                  {selectedEnumValue === opt.value && (
                    <Check className="mr-2 h-3.5 w-3.5 text-primary" />
                  )}                 
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
